import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { createLeadRegistry, getLeadSubStatuses, patchLead, logError } from '../api';
import { getUserId, getSalesRepId, isComercial } from '../services/AuthService';

const LeadStatusChangeModal = ({ show, handleClose, lead, newStatusId, statusName, onStatusChanged }) => {
    const navigate = useNavigate();
    const [subTypeId, setSubTypeId] = useState('');
    const [comments, setComments] = useState('');
    const [followUpDate, setFollowUpDate] = useState('');
    const [subTypes, setSubTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateOrder, setShowCreateOrder] = useState(false);

    useEffect(() => {
        if (show && newStatusId) {
            loadSubTypes();
            resetForm();
        }
    }, [show, newStatusId]);

    const loadSubTypes = async () => {
        try {
            const subTypesData = await getLeadSubStatuses(newStatusId);
            setSubTypes(subTypesData || []);
        } catch (error) {
            logError(error);
            console.error('Error loading sub types:', error);
            setSubTypes([]);
        }
    };

    const resetForm = () => {
        setSubTypeId('');
        setComments('');
        setFollowUpDate('');
        setShowCreateOrder(false);
    };

    const handleCreateOrder = () => {
        handleClose();
        navigate('/orders/new', { state: { leadData: lead } });
    };

    const handleSkipOrder = () => {
        handleClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Actualizar el estado del lead
            const leadUpdate = {
                statusId: newStatusId,
                subStatusId: subTypes.length > 0 ? (subTypeId ? parseInt(subTypeId) : null) : null
            };
            await patchLead(lead.id, leadUpdate);

            // Crear el registro
            const leadRegistry = {
                leadId: parseInt(lead.id),
                registryTypeId: parseInt(newStatusId),
                subStatusId: subTypes.length > 0 ? (subTypeId ? parseInt(subTypeId) : null) : null,
                salesRepId: getSalesRepId() || null,
                createdByUserId: getUserId(),
                comments: comments.trim() || null,
                followUpDate: followUpDate || null
            };

            await createLeadRegistry(leadRegistry);
            onStatusChanged();
            
            // Si es cambio a Venta (statusId = 3) y es comercial, mostrar opción de crear pedido
            if (newStatusId === 3 && isComercial()) {
                setShowCreateOrder(true);
            } else {
                handleClose();
            }
        } catch (error) {
            logError(error);
            console.error('Error changing lead status:', error);
        } finally {
            setLoading(false);
        }
    };

    if (showCreateOrder) {
        return (
            <Modal show={show} onHide={handleClose} size="md" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>¡Venta Registrada!</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <div className="mb-4">
                        <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h5 className="mb-3">El lead se ha marcado como Venta exitosamente</h5>
                    <p className="text-muted mb-4">¿Deseas crear un pedido para este cliente ahora?</p>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button variant="success" onClick={handleCreateOrder}>
                        <i className="bi bi-plus-circle me-1"></i>
                        Crear Pedido
                    </Button>
                    <Button variant="secondary" onClick={handleSkipOrder}>
                        Ahora No
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Cambiar Estado a: {statusName}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Row>
                        {subTypes.length > 0 && (
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Detalle de Estado</Form.Label>
                                    <Form.Select
                                        value={subTypeId}
                                        onChange={(e) => setSubTypeId(e.target.value)}
                                        required
                                    >
                                        <option value="">Seleccionar detalle...</option>
                                        {subTypes.map(subType => (
                                            <option key={subType.valueId} value={subType.valueId}>
                                                {subType.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        )}
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label>Comentarios (opcional)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Comentarios o notas..."
                            maxLength={255}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Fecha de Seguimiento</Form.Label>
                        <Form.Control
                            type="date"
                            value={followUpDate}
                            onChange={(e) => setFollowUpDate(e.target.value)}
                            onClick={(e) => e.target.showPicker && e.target.showPicker()}
                            style={{ cursor: 'pointer' }}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="primary" 
                        type="submit" 
                        disabled={loading || (subTypes.length > 0 && !subTypeId)}
                    >
                        {loading ? 'Guardando...' : 'Cambiar Estado'}
                    </Button>
                    <Button 
                        variant="secondary" 
                        onClick={handleClose}
                    >
                        Cancelar
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default LeadStatusChangeModal;